import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Paperclip, FileText, Image as ImageIcon, Send, Bell, Check,
  Plus, Pencil, Trash2, Sparkles, CheckCircle2, Activity, RotateCcw,
} from "lucide-react";
import { Avatar, Badge, Tag, relTime } from "@/components/library/badges";
import { EmptyState } from "@/components/library/states";

// AttachmentGallery — images/files with AI-generated tags.
export function AttachmentGallery({ attachments = [], onAdd, canAdd, testid = "attachment-gallery" }) {
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {attachments.map((a) => {
          const isImg = (a.mime_type || "").startsWith("image/");
          return (
            <div key={a.id} data-testid={`attachment-${a.id}`} className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="aspect-video grid place-items-center bg-secondary text-muted-foreground">
                {isImg ? (a.url ? <img src={a.url} alt={a.file_name} className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6" />) : <FileText className="h-6 w-6" />}
              </div>
              <div className="p-2.5">
                <p className="text-xs font-medium truncate">{a.file_name}</p>
                {a.ai_tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-1.5">{a.ai_tags.map((t) => <Tag key={t}>{t}</Tag>)}</div>}
              </div>
            </div>
          );
        })}
        {canAdd && (
          <button onClick={onAdd} data-testid="attachment-add"
            className="aspect-video rounded-xl border border-dashed border-border grid place-items-center text-muted-foreground hover:border-accent hover:text-accent transition-colors">
            <Paperclip className="h-5 w-5" />
          </button>
        )}
      </div>
      {attachments.length === 0 && !canAdd && <EmptyState title="Inga bilagor" />}
    </div>
  );
}

// CommentThread — list + composer. Parent owns persistence.
export function CommentThread({ comments = [], canComment, onSubmit, testid = "comment-thread" }) {
  const [text, setText] = useState("");
  const submit = (e) => { e.preventDefault(); if (text.trim()) { onSubmit?.(text.trim()); setText(""); } };
  return (
    <div data-testid={testid} className="flex flex-col gap-4">
      <div className="space-y-4">
        {comments.length === 0 && <p className="text-sm text-muted-foreground">Inga kommentarer ännu.</p>}
        {comments.map((c) => (
          <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
            <Avatar name={c.author} size={32} />
            <div className="min-w-0">
              <p className="text-sm"><span className="font-medium">{c.author}</span>
                <span className="text-muted-foreground text-xs font-mono ml-2">{relTime(c.created_at)}</span></p>
              <p className="text-sm text-muted-foreground mt-0.5">{c.body}</p>
            </div>
          </div>
        ))}
      </div>
      {canComment && (
        <form onSubmit={submit} className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} data-testid="comment-input"
            placeholder="Skriv en kommentar…"
            className="flex-1 bg-secondary/60 border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-accent transition-colors" />
          <button type="submit" data-testid="comment-submit"
            className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground hover:opacity-90 active:scale-95 transition-all">
            <Send className="h-4 w-4" />
          </button>
        </form>
      )}
    </div>
  );
}

const ACTION_ICON = { create: Plus, update: Pencil, delete: Trash2, ai_analyze: Sparkles, complete: CheckCircle2, restore: RotateCcw, recompute: RotateCcw };
const ACTION_LABEL = { create: "skapade", update: "uppdaterade", delete: "tog bort", ai_analyze: "AI-analyserade", complete: "slutförde", approve: "godkände", restore: "återställde", recompute: "räknade om" };

// AuditTimeline — immutable change history with diffs.
export function AuditTimeline({ events = [], testid = "audit-timeline" }) {
  if (!events.length) return <EmptyState title="Ingen historik" />;
  return (
    <div data-testid={testid} className="relative pl-6">
      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
      {events.map((e) => {
        const I = ACTION_ICON[e.action] || Activity;
        return (
          <div key={e.id} className="relative pb-5" data-testid={`audit-${e.id}`}>
            <div className="absolute -left-6 top-0.5 h-4 w-4 rounded-full bg-card border border-border grid place-items-center">
              <I className="h-2.5 w-2.5 text-muted-foreground" />
            </div>
            <div className="flex items-center gap-2">
              <Avatar name={e.actor_name} size={22} />
              <p className="text-sm"><span className="font-medium">{e.actor_name}</span> {ACTION_LABEL[e.action] || e.action} {e.entity_type}</p>
            </div>
            {e.diff && (
              <div className="mt-1 text-xs text-muted-foreground font-mono space-y-0.5">
                {Object.entries(e.diff).map(([k, v]) => <div key={k}>{k}: {String(v.from)} → {String(v.to)}</div>)}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-0.5 font-mono">{relTime(e.created_at)}</p>
          </div>
        );
      })}
    </div>
  );
}

// ActivityFeed — recent events stream.
export function ActivityFeed({ items = [], testid = "activity-feed" }) {
  if (!items.length) return <EmptyState title="Ingen aktivitet" />;
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-2xl divide-y divide-border">
      {items.map((e) => {
        return (
          <div key={e.id} className="flex gap-3 p-4">
            <Avatar name={e.actor_name} size={30} />
            <div>
              <p className="text-sm">{e.message || `${e.actor_name} ${ACTION_LABEL[e.action] || e.action} ${e.entity_type}`}</p>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">{relTime(e.created_at)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// NotificationCenter — bell + dropdown with unread state.
export function NotificationCenter({ notifications = [], onRead, onSelect, testid = "notification-center" }) {
  const [open, setOpen] = useState(false);
  const unread = notifications.filter((n) => !n.read).length;
  return (
    <div className="relative" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} data-testid="notification-bell"
        className="relative grid place-items-center h-9 w-9 rounded-xl bg-secondary/60 border border-border text-muted-foreground hover:text-foreground transition-colors">
        <Bell className="h-4 w-4" />
        {unread > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 grid place-items-center rounded-full bg-accent text-white text-[0.6rem] font-bold">{unread}</span>}
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="absolute right-0 mt-2 w-80 z-50 bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <span className="font-display font-semibold text-sm">Notiser</span>
                {unread > 0 && <Badge tone="accent">{unread} nya</Badge>}
              </div>
              <div className="max-h-80 overflow-y-auto divide-y divide-border">
                {notifications.length === 0 && <p className="p-4 text-sm text-muted-foreground">Inga notiser.</p>}
                {notifications.map((n) => (
                  <button key={n.id} onClick={() => { onRead?.(n.id); onSelect?.(n); setOpen(false); }} data-testid={`notification-${n.id}`}
                    className={`w-full text-left p-4 hover:bg-secondary/60 transition-colors ${n.read ? "opacity-60" : ""}`}>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="h-2 w-2 rounded-full bg-accent mt-1.5 shrink-0" />}
                      <div className={n.read ? "" : "-ml-0"}>
                        <p className="text-sm font-medium">{n.title}</p>
                        <p className="text-xs text-muted-foreground">{n.body}</p>
                        <p className="text-[0.7rem] text-muted-foreground mt-1 font-mono">{relTime(n.created_at)}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
